import { router } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import actionCableConsumer from '../lib/actionCableConsumer';

export const useLocationUpdates = (initialLocations) => {
  const [locations, setLocations] = useState(initialLocations || []);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [lastUpdate, setLastUpdate] = useState(null);
  const subscriptionRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const subscriptionId = useRef(`location_updates_${Date.now()}_${Math.random()}`);
  const disconnectTimeoutRef = useRef(null);

  // Garantir que as localizações iniciais sejam sempre carregadas
  useEffect(() => {
    if (initialLocations && initialLocations.length > 0) {
      setLocations(initialLocations);
    }
  }, [initialLocations]);

  // Função para atualizar locations baseado no tipo de broadcast
  const updateLocations = (data) => {
    const { type, location } = data;

    setLocations(currentLocations => {
      let updatedLocations = [...currentLocations];

      switch (type) {
        case 'location_created':
          // Adicionar nova localização se não existir
          if (!updatedLocations.find(l => l.id === location.id)) {
            updatedLocations.push(location);
          }
          break;

        case 'location_updated':
          // Atualizar localização existente ou adicionar se não existir
          const updateIndex = updatedLocations.findIndex(l => l.user_id === location.user_id);
          if (updateIndex !== -1) {
            updatedLocations[updateIndex] = location;
          } else {
            updatedLocations.push(location);
          }
          break;

        case 'location_removed':
          // Remover localização
          updatedLocations = updatedLocations.filter(l => l.id !== location.id);
          break;

        default:
          console.warn('Unknown location update type:', type);
      }

      return updatedLocations;
    });

    setLastUpdate(new Date());
  };

  // Função para lidar com desconexão prolongada
  const handleDisconnectTimeout = () => {
    if (isUnmountedRef.current) return;

    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
    }

    disconnectTimeoutRef.current = setTimeout(() => {
      if (isUnmountedRef.current) return;
      console.log('Disconnected for more than 1 minute, reloading page...');
      router.reload();
    }, 60000); // 1 minuto
  };

  // Função para limpar timeout de desconexão
  const clearDisconnectTimeout = () => {
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
  };

  // Função para inicializar conexão usando consumer global
  const initializeConnection = () => {
    if (isUnmountedRef.current) return;

    // Registrar callback para mudanças de status
    actionCableConsumer.onStatusChange(subscriptionId.current, (status) => {
      if (isUnmountedRef.current) return;
      setConnectionStatus(status);
    });

    // Criar subscription usando consumer global
    const subscription = actionCableConsumer.createSubscription(
      'LocationsChannel',
      subscriptionId.current,
      {
        connected() {
          if (isUnmountedRef.current) return;
          console.log('Connected to LocationsChannel via global consumer');
          clearDisconnectTimeout();
        },

        disconnected() {
          if (isUnmountedRef.current) return;
          console.log('Disconnected from LocationsChannel');
          handleDisconnectTimeout();
        },

        received(data) {
          if (isUnmountedRef.current) return;
          console.log('Received data from LocationsChannel:', data);
          updateLocations(data);
        },

        rejected() {
          if (isUnmountedRef.current) return;
          console.log('LocationsChannel subscription rejected');
        }
      }
    );

    subscriptionRef.current = subscription;
  };

  // Função para cleanup completo
  const cleanup = () => {
    console.log('Cleaning up useLocationUpdates...');

    clearDisconnectTimeout();

    // Remover subscription específica
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    // Remover callback de status
    actionCableConsumer.removeStatusCallback(subscriptionId.current);
  };

  // Efeito para gerenciar conexão
  useEffect(() => {
    initializeConnection();

    return cleanup;
  }, []);

  // Cleanup no unmount
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      cleanup();
    };
  }, []);

  // Verificar se tempo real está habilitado
  const isRealTimeEnabled = true; // Sempre habilitado para localizações

  return {
    locations,
    connectionStatus,
    lastUpdate,
    isRealTimeEnabled
  };
}; 